import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { tpoGenUrls } from 'config/urls';
import { tpoGenKeys } from '../../../../utils/tpoGenQueryKeys';
import { useEffect, useMemo, useState } from 'react';
import type { ApiPPG, MetricValue } from '../../../../types/promoMetrics';
import { useTpoGenConfig } from '../../../../context/CustomerConfigContext';
import { buildPromoFormValues } from '../../../../utils/buildPromoPayload';
import type { DimSelectFieldConfig } from '../../../../config/tpoGenConfig';

interface PromoApiPPG {
    ppg_id: string;
    promotion_id: string;
    event_id: string;
    plan_id: string;
    metrics: Record<string, MetricValue>;
    [key: string]: unknown;
}

interface PromoApiResponse {
    promotion_id: string;
    event_id: string;
    plan_id: string;
    attributes: Record<string, unknown>;
    name: string;
    start_date: string;
    end_date: string;
    cra_start_date: string;
    cra_end_date: string;
    status: string;
    offer: { offer_type: string } | null;
    ppgs: PromoApiPPG[];
    applicable_metric_keys?: string[];
    default_visible_metric_keys?: string[];
}

const fetchPromoById = async (promotionId: string, signal?: AbortSignal) => {
    const token = localStorage.getItem('accessToken');
    const response = await axios(tpoGenUrls.getPromoById(promotionId), {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${token}`,
        },
        signal,
    });
    return response.data as PromoApiResponse;
};

const useGetPromoData = (promotionId: string | null, isActive = true) => {
    const { promoFields, entityAttributes, fieldRules, promoDetailsFields } = useTpoGenConfig();
    const queryClient = useQueryClient();
    const { data, isPending, isFetching, isError } = useQuery({
        queryKey: tpoGenKeys.promoDetails(promotionId ?? ''),
        queryFn: ({ signal }) => fetchPromoById(promotionId as string, signal),
        enabled: !!promotionId,
    });

    // True only while the on-tab-open refetch (below) is in flight, so the UI can
    // show a loader for the post-event-update refetch specifically — not for other
    // background fetches (`isFetching` would also flip on e.g. window-focus refetch).
    const [isRefetchingAfterUpdate, setIsRefetchingAfterUpdate] = useState(false);

    // When this promo tab becomes active, refetch its data if it was invalidated
    // since it was last fetched (e.g. the parent event was updated while this tab
    // stayed mounted in the background). The invalidation is marked without an
    // automatic refetch in useCreateEventMutation, so the refetch happens here,
    // only on tab open. react-query clears `isInvalidated` after the refetch, so
    // this is self-resetting and fires once per invalidation.
    useEffect(() => {
        if (!isActive || !promotionId) return;
        const state = queryClient.getQueryState(tpoGenKeys.promoDetails(promotionId));
        if (state?.isInvalidated) {
            setIsRefetchingAfterUpdate(true);
            queryClient
                .refetchQueries({ queryKey: tpoGenKeys.promoDetails(promotionId) })
                .finally(() => setIsRefetchingAfterUpdate(false));
        }
    }, [isActive, promotionId, queryClient]);

    const { ppgIdKey, ppgNameKey } = useMemo(() => {
        const lowestField = promoDetailsFields.find(
            (f): f is DimSelectFieldConfig => f.type === 'dimSelect' && (f as DimSelectFieldConfig).lowestAttribute,
        );
        const dim = lowestField?.dimension ?? 'dim1';
        return { ppgIdKey: `${dim}_id`, ppgNameKey: `${dim}_name` };
    }, [promoDetailsFields]);

    const promoFormValues = useMemo(() => {
        if (!data) return null;
        return buildPromoFormValues(
            entityAttributes,
            promoFields,
            data as unknown as Record<string, unknown>,
            fieldRules,
        );
    }, [data, promoFields, entityAttributes, fieldRules]);

    const ppgData: ApiPPG[] = useMemo(() => {
        if (!data?.ppgs) return [];
        return data.ppgs.map((ppg) => ({
            ppg_id: ppg[ppgIdKey] as number,
            ppg_name: ppg[ppgNameKey] as string,
            metrics: ppg.metrics,
        }));
    }, [data, ppgIdKey, ppgNameKey]);

    const ppgIds: number[] = useMemo(() => {
        if (!data?.ppgs) return [];
        return data.ppgs.map((ppg) => ppg[ppgIdKey] as number);
    }, [data, ppgIdKey]);

    const promoMeta = useMemo(() => {
        if (!data) return null;
        return {
            promotion_id: data.promotion_id,
            event_id: data.event_id,
            plan_id: data.plan_id,
            status: data.status,
        };
    }, [data]);

    // Server-computed visibility for the saved promo's cross-section. `null` => backend omitted it.
    const { applicableMetricKeys, defaultVisibleMetricKeys } = useMemo(
        () => ({
            applicableMetricKeys: data?.applicable_metric_keys ?? null,
            defaultVisibleMetricKeys: data?.default_visible_metric_keys ?? null,
        }),
        [data],
    );

    return {
        promoFormValues,
        ppgData,
        ppgIds,
        promoMeta,
        applicableMetricKeys,
        defaultVisibleMetricKeys,
        isPending: !!promotionId && isPending,
        isFetching,
        isRefetchingAfterUpdate,
        isError,
    };
};

export default useGetPromoData;
